import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';

/**
 * Structured address input with autocomplete via the French BAN
 * (Base Adresse Nationale — api-adresse.data.gouv.fr).
 *
 * value: { street, postal_code, city }
 * onChange: (next) => void
 */
export default function AddressAutocomplete({ value, onChange, testIdPrefix = 'address' }) {
  const v = value || { street: '', postal_code: '', city: '' };
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const searchBan = async (q) => {
    if (!q || q.trim().length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=6`);
      const data = await res.json();
      setSuggestions((data.features || []).map((f) => ({
        label: f.properties.label,
        street: `${f.properties.housenumber || ''} ${f.properties.street || f.properties.name || ''}`.trim(),
        postal_code: f.properties.postcode || '',
        city: f.properties.city || '',
      })));
    } catch (e) {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const onStreetChange = (e) => {
    const val = e.target.value;
    onChange({ ...v, street: val });
    setQuery(val);
    setOpen(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => searchBan(val), 250);
  };

  const pick = (s) => {
    onChange({ street: s.street, postal_code: s.postal_code, city: s.city });
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="space-y-2" ref={ref}>
      <div className="relative">
        <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-[#4F46E5]" /> Rue
          <span className="text-[10px] font-normal text-[#8E8FA5] ml-1">(auto-complétion France)</span>
        </label>
        <input
          data-testid={`${testIdPrefix}-street`}
          value={v.street}
          onChange={onStreetChange}
          onFocus={() => query && setOpen(true)}
          placeholder="12 rue de la République"
          className="w-full h-10 px-3 pr-9 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-[36px] text-[#8E8FA5]" />}
        {open && suggestions.length > 0 && (
          <div className="absolute z-40 mt-1 w-full bg-white rounded-xl border border-[#EFF0F6] shadow-lg max-h-[220px] overflow-y-auto" style={{ boxShadow: '0 6px 24px rgba(26,27,46,0.12)' }}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                data-testid={`${testIdPrefix}-suggestion-${i}`}
                onClick={() => pick(s)}
                className="w-full text-left text-[13px] px-3 py-2 hover:bg-[#EEF2FF] border-b border-[#F3F4F8] last:border-0"
              >
                <div className="font-semibold text-[#1A1B2E] truncate">{s.label}</div>
                <div className="text-[11.5px] text-[#8E8FA5]">{s.postal_code} · {s.city}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-[140px_1fr] gap-2">
        <div>
          <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Code postal</label>
          <input
            data-testid={`${testIdPrefix}-postal`}
            value={v.postal_code}
            onChange={(e) => onChange({ ...v, postal_code: e.target.value })}
            placeholder="75011"
            className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
          />
        </div>
        <div>
          <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Ville</label>
          <input
            data-testid={`${testIdPrefix}-city`}
            value={v.city}
            onChange={(e) => onChange({ ...v, city: e.target.value })}
            placeholder="Paris"
            className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
          />
        </div>
      </div>
    </div>
  );
}

/** Format address object → single line string for display/legacy storage */
export const formatAddress = (addr) => {
  if (!addr) return '';
  const parts = [addr.street, [addr.postal_code, addr.city].filter(Boolean).join(' ')];
  return parts.filter((p) => p && p.trim()).join(', ');
};
